import React, { useContext, useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { VerticalFieldAndValueWithIcon } from '@components/ringil3/Containers/VerticalFieldAndValueWithIcon';
import {
  ApiTypeEnum,
  ApiTypesEnum,
  AttachmentUploadedOutDTO,
  CarrierOrGroupEnum,
  CurrencyLockTypeEnum,
  DachserDeliveryTermEnum,
  DachserProductTypeEnum,
  EntityTypeEnum,
  ExpeditionDetailV4OutDTO,
  FeaturesEnum,
  FindCarrierTypeEnum,
  GetPreviousDistributionInnerResponseOutDTO,
  PermissionTypesEnum,
  SchenkerAddonsTypeEnum,
  SchenkerProductTypeEnum,
  SelectedItemTypeEnum,
} from '@api/ringil-api.v2';
import { ApiContext } from '@api/api';
import useForm from '@hooks/useForm/useForm';
import * as yup from 'yup';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import { AiCarrierAutoSelectionDto } from '@components/ai/AiCarrierAutoSelection.tsx';
import CarrierDistributionSelector, {
  CarrierDistributionSelectorData,
  DistributionType,
} from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';

interface ComponentProps {
  onContinue: () => void;
  aiCarrierAutoSelectionDto?: AiCarrierAutoSelectionDto;
  expedition: ExpeditionDetailV4OutDTO;
}

interface FormData {
  distribution: CarrierDistributionSelectorData;
}

export const useCreateAddMoreCarriers = () => {
  const [visible, setVisible] = useState(false);

  const AddMoreCarriers: React.FC<ComponentProps> = ({ onContinue, aiCarrierAutoSelectionDto, expedition }) => {
    const { tr } = useTranslationLgs();
    const { loggedUser } = useContext(AuthenticatedUserContext);
    const {
      cuAddMoreCarriersToDistribution,
      getPreviousDistribution,
      listDocumentsForEntity,
    } = useContext(ApiContext);
    const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
    const [loading, setLoading] = useState<boolean>(false);
    const [originalDistribution, setOriginalDistribution] = useState<CarrierDistributionSelectorData>(null);
    const [attachments, setAttachments] = useState<AttachmentUploadedOutDTO[]>([]);

    const originalDistributionAllowed = useSecuredFlag('addmore-original-distribution', {
      kind: 'visibility',
      description: 'Load original distribution in add-more-carriers sidebar',
      permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_CREATE_DISTRIBUTION_RW],
    }).granted;

    useEffect(() => {
      listDocumentsForEntity(EntityTypeEnum.EXPEDITION, expedition.id, d => {
        setAttachments(d);
      });
    }, []);

    if (!visible) return null;

    const getOriginalDistribution = (distribution?: GetPreviousDistributionInnerResponseOutDTO): CarrierDistributionSelectorData | null => {
      if (!distribution) return null;

      const fixedOrSpot = distribution.spot || distribution.fixed;

      const selectedItems = fixedOrSpot?.selectedItems?.map(item => ({
        id: item.id,
        userIds: item.userId ? [item.userId] : null,
        apiTypes: item.apiType ? [item.apiType as unknown as ApiTypesEnum] : null,
        carrierType: item.selectedItemType === SelectedItemTypeEnum.API
          ? FindCarrierTypeEnum.API
          : item.selectedItemType === SelectedItemTypeEnum.GROUP
            ? FindCarrierTypeEnum.GROUP
            : item.selectedItemType === SelectedItemTypeEnum.CARRIER && item.userId && item.userId.length > 0
              ? FindCarrierTypeEnum.CARRIER_USER
              : FindCarrierTypeEnum.CARRIER,
        _dachserProductTypeCode: fixedOrSpot.dachserProductType,
        _dachserDeliveryTermCode: fixedOrSpot.dachserDeliveryTerm,
        _schenkerProductTypeCode: fixedOrSpot.schenkerProductType,
        _schenkerAddonsTypeCodes: fixedOrSpot.schenkerAddonsType,
      })) || (distribution.assignedCarrierId ? [{ id: distribution.assignedCarrierId }] : []);

      return {
        distributionType: DistributionType.SPOT,
        selectedItems,
      };
    };

    useEffect(() => {
      if (visible && expedition && originalDistributionAllowed) {
        getPreviousDistribution(
          expedition.id,
          (data) => {
            if (data && data.previousDistribution) {
              setOriginalDistribution(getOriginalDistribution(data.previousDistribution));
            }
          },
        );
      }
    }, [expedition, visible, originalDistributionAllowed]);

    const currencyLockEnabled = useSecuredFlag('addmore-currency-lock', {
      kind: 'feature',
      description: 'Currency lock in add-more-carriers sidebar',
      features: [FeaturesEnum.CURRENCY_LOCK],
    }).granted;

    const initDistribution = (): CarrierDistributionSelectorData => {
      return {
        distributionType: DistributionType.SPOT,
        selectedItems: [],
        price: { currency: loggedUser?.defaultCurrencyIsoCode, price: 0 },
        notifyAssignedCarrier: false,
        currencyLockType: currencyLockEnabled ? CurrencyLockTypeEnum.ONLY_SELECTED : CurrencyLockTypeEnum.NONE,
      };
    };

    const validationRules = yup.object().shape({
      distribution: yup.object().shape({
        distributionType: yup.string(),
        selectedItems: yup.array().when('distributionType', {
          is: (type: string) => type === 'FIXED' || type === 'SPOT' || type === 'ASSIGN',
          then: yup.array()
            .min(1, tr('RequestCarrierSidebar.mandatoryCarriers', 'Please select at least one carrier'))
            .required(tr('RequestCarrierSidebar.mandatoryCarriers', 'Please select at least one carrier')),
          otherwise: yup.array().nullable(),
        }),
      }).nullable(),
    });

    const form = useForm<FormData>(
      validationRules,
      {
        distribution: initDistribution(),
      },
      d => onContinueHandler(d.distribution),
      false,
      false,
    );

    const onContinueHandler = (data: CarrierDistributionSelectorData) => {
      setLoading(true);

      const dachserItem = data?.selectedItems?.find(item => item.apiTypes?.includes(ApiTypesEnum.DACHSER));
      const schenkerItem = data?.selectedItems?.find(item => item.apiTypes?.includes(ApiTypesEnum.SCHENKER));

      cuAddMoreCarriersToDistribution(
        expedition.customerId,
        expedition.applicationId,
        {
          carriers: data.selectedItems.map(carrier => {
            let selectedItemType: SelectedItemTypeEnum;
            switch (carrier.carrierType) {
              case 'GROUP':
                selectedItemType = SelectedItemTypeEnum.GROUP;
                break;
              case FindCarrierTypeEnum.API:
                selectedItemType = SelectedItemTypeEnum.API;
                break;
              default:
                selectedItemType = SelectedItemTypeEnum.CARRIER;
            }
            return {
              id: carrier.id,
              carrierOrGroup: carrier.carrierType === 'CARRIER' || carrier.carrierType === 'CARRIER_USER'
                ? CarrierOrGroupEnum.CARRIER
                : CarrierOrGroupEnum.GROUP,
              selectedItemType: selectedItemType,
              apiType: carrier.apiTypes && carrier.apiTypes.length > 0 ? carrier.apiTypes[0] as unknown as ApiTypeEnum : null,
              userId: Array.isArray(carrier.userIds) && carrier.userIds.length > 0 ? carrier.userIds[0] : null,
            };
          }),
          dachserProductType: dachserItem?._dachserProductTypeCode as DachserProductTypeEnum,
          dachserDeliveryTerm: dachserItem?._dachserDeliveryTermCode as DachserDeliveryTermEnum,
          schenkerProductType: schenkerItem?._schenkerProductTypeCode as SchenkerProductTypeEnum,
          schenkerAddonsType : schenkerItem?._schenkerAddonsTypeCodes as SchenkerAddonsTypeEnum[],
        },
        () => {
          setLoading(false);
          setVisible(false);
          onContinue();
        },
        null,
        {
          onValidationFailed: (errors) => {
            setLoading(false);
            setBeValidationErrors(errors);
          },
        },
      );
    };

    return (
      <>
        <RingilSidebar
          visible={visible}
          onBack={() => setVisible(false)}
          onContinue={() => form.validateAndSend()}
          heading={tr(`AddMoreCarriers.heading`, `Add more carriers`)}
          loading={loading}
        >
          <BackendValidationComponent />
          <VerticalFieldAndValueWithIcon
            label={tr(`AddMoreCarriers.forExpedition`, `For expedition`)}
            valueText={expedition.applicationId}
            iconPath={'/images/icons/ringil3/forExpedition.svg'}
          />

          <CarrierDistributionSelector
            allowedDistributionTypes={[]}
            pickupTimezoneId={expedition?.locations?.[0]?.timezoneId}
            distribution={form.values.distribution}
            originalDistribution={originalDistribution}
            validationErrors={form.validationErrors
              .filter(e => e.path?.startsWith('distribution'))
              .map(e => ({ path: e.path || '', message: e.message }))}
            onChange={v => form.setFieldValue(form.names.distribution, v)}
            aiCarrierAutoSelectionDto={aiCarrierAutoSelectionDto}
            onlyCarriers
            expeditionValidations={[
              {
                locations: expedition?.locations
                  .map(loc => ({
                    id: loc.id,
                    companyLocationId: loc.companyLocationId,
                    city: loc.city,
                    street: loc.streetNr,
                    country: loc.country,
                    postalCode: loc.postalCode,
                    orders: loc.loadedOrders?.map(order => ({
                      items: order.items?.map((item) => ({
                        length: item.length,
                        width: item.width,
                        height: item.height,
                        weight: item.weight,
                        amount: item.amount,
                      })),
                      loadingLocationId: loc.id,
                      unloadingLocationId: expedition?.locations.filter(loc => loc.unloadedOrders?.some(o => o.id === order.id))[0]?.id,
                      totalWeight: order.orderTotalWeight,
                    })),
                    contactEmail: loc.selectedPerson?.contactEmail,
                    contactName: loc.selectedPerson?.contactName,
                    contactPhone: loc.selectedPerson?.contactPhone,
                    deliverySlot: loc.deliverySlot?.dayWithInterval ? {
                      days: loc.deliverySlot.dayWithInterval,
                    } : null,
                  })),
                cargoType: expedition?.cargoTypeCode ? {
                  cargoTypeCode: expedition.cargoTypeCode,
                } : null,
                incotermsValue: expedition?.incoterms?.incotermsValue,
                incotermsDescription: expedition?.incoterms?.incotermsDescription,
                attachments: attachments.map(attachment => ({
                  documentType: attachment.documentType,
                  contentType: attachment.contentType,
                })),
              },
            ]}
          />
        </RingilSidebar>
      </>
    );
  };

  return { AddMoreCarriers, setVisible };
};
